<template>
  <div class="pa-2">
    <system-health-banner
      title="Pricing Loop"
      :subtitle="bannerSubtitle"
      :status="summary.status || 'unknown'"
    />

    <div class="mb-2">
      <status-chip
        :status="summary.status || 'unknown'"
        :label="'Queue: ' + queueSummaryLabel"
      />
      <status-chip
        :status="activeTripsChipStatus"
        :label="'Active trips: ' + (summary.activeTrips || 0)"
      />
      <status-chip
        :status="mapApiDependencyStatus"
        :label="'Map API geo backup: ' + mapApiDependencyLabel"
      />
      <status-chip
        :status="stuckChipStatus"
        :label="'Stuck: ' + (summary.stuckCount || 0)"
      />
    </div>

    <v-btn
      small
      outline
      color="primary"
      class="mb-2 mr-2"
      @click="$emit('navigate-map-api')"
    >
      View Map API geo backup
    </v-btn>

    <v-btn
      outline
      color="primary"
      :loading="apiInProgress"
      :disabled="apiInProgress"
      @click="detailDialog = true"
    >
      View per-bus pricing details
    </v-btn>

    <detail-dialog
      v-model="detailDialog"
      title="Pricing Loop Details"
      :max-width="1200"
    >
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="apiInProgress"
        :pagination.sync="pagination"
        :total-items="totalItems"
        :rows-per-page-items="[10, 25, 50, 100]"
        @update:pagination="onPaginationChange"
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>{{ props.item.transportationLabel }}</td>
            <td>{{ props.item.status }}</td>
            <td>{{ getDecisionCount(props.item, 'priced') }}</td>
            <td>{{ getDecisionCount(props.item, 'noop') }}</td>
            <td>{{ getMapApiFailureCount(props.item) }}</td>
            <td>{{ formatAge(props.item.queueAgeSeconds) }}</td>
            <td class="text-xs-right">
              <v-btn
                small
                color="warning"
                outline
                :loading="isResetting(props.item.transportationProfileId)"
                :disabled="!props.item.isStuck || isResetting(props.item.transportationProfileId)"
                @click="resetRow(props.item)"
              >
                Reset to pending
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </detail-dialog>
  </div>
</template>

<script>
import {
  getApiProgressivePricingQueue,
  resetApiProgressivePricingQueue,
  getMapApiOverview
} from '@/services/diagnostics-service'
import { addAlert } from '@/utilities/helpers'
import SystemHealthBanner from '../components/SystemHealthBanner.vue'
import StatusChip from '../components/StatusChip.vue'
import DetailDialog from '../components/DetailDialog.vue'
import { formatAgeShort } from '../components/status-mapping'

export default {
  name: 'realtime-diagnostics-pricing-tab',
  components: {
    SystemHealthBanner,
    StatusChip,
    DetailDialog
  },
  props: {
    refreshToken: {
      type: Number,
      default: 0
    },
    silentRefresh: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      apiInProgress: false,
      detailDialog: false,
      resettingId: null,
      items: [],
      totalItems: 0,
      summary: {
        status: 'unknown',
        queue: {},
        activeTrips: 0,
        stuckCount: 0
      },
      mapApiDependencyStatus: 'unknown',
      mapApiDependencyLabel: 'Unknown freshness',
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 25,
        sortBy: 'dateUpdated'
      },
      headers: [
        { text: 'Bus', value: 'transportationLabel' },
        { text: 'Status', value: 'status' },
        { text: 'Priced', value: 'pricedCount', sortable: false },
        { text: 'No-op', value: 'noopCount', sortable: false },
        { text: 'Map API Failures', value: 'mapApiFailures', sortable: false },
        { text: 'Queue Age', value: 'queueAgeSeconds', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    bannerSubtitle () {
      return 'Last activity: ' + formatAgeShort(this.summary.lastActivityAgeSeconds)
    },
    queueSummaryLabel () {
      const queue = this.summary.queue || {}
      return (queue.pending || 0) + ' pending • ' + (queue.processing || 0) + ' processing • ' + (queue.failed || 0) + ' failed'
    },
    activeTripsChipStatus () {
      const activeTrips = this.summary.activeTrips || 0
      if (activeTrips > 0) {
        return 'healthy'
      }
      return 'stale'
    },
    stuckChipStatus () {
      const count = this.summary.stuckCount || 0
      return count > 0 ? 'critical' : 'healthy'
    }
  },
  watch: {
    refreshToken: {
      immediate: true,
      handler () {
        this.loadData(this.silentRefresh)
      }
    }
  },
  methods: {
    async loadData (silent) {
      if (!silent) {
        this.apiInProgress = true
      }

      try {
        const params = {
          limit: this.pagination.rowsPerPage || 25,
          offset: ((this.pagination.page || 1) - 1) * (this.pagination.rowsPerPage || 25)
        }
        const responses = await Promise.all([
          getApiProgressivePricingQueue(params),
          getMapApiOverview()
        ])
        const pricingResponse = responses[0]
        const mapOverview = responses[1]
        this.items = pricingResponse && pricingResponse.items ? pricingResponse.items : []
        this.totalItems = pricingResponse && pricingResponse.pagination ? pricingResponse.pagination.total || 0 : 0
        this.summary = pricingResponse && pricingResponse.summary ? pricingResponse.summary : this.summary
        const geoBackup = mapOverview && mapOverview.systems && mapOverview.systems.geoBackup
          ? mapOverview.systems.geoBackup
          : null
        this.mapApiDependencyStatus = geoBackup && geoBackup.status ? geoBackup.status : 'unknown'
        this.mapApiDependencyLabel = geoBackup
          ? ('last persisted ' + formatAgeShort(geoBackup.lastPersistedAgeSeconds) + ' ago')
          : 'Unknown freshness'
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to load pricing diagnostics'
        })
      } finally {
        if (!silent) {
          this.apiInProgress = false
        }
      }
    },
    onPaginationChange () {
      this.loadData(false)
    },
    formatAge (seconds) {
      return formatAgeShort(seconds)
    },
    isResetting (id) {
      return this.resettingId && String(this.resettingId) === String(id)
    },
    getDecisionCount (item, type) {
      const metadata = item && item.decisionMetadata ? item.decisionMetadata : {}
      if (type === 'priced') {
        return Number(metadata.pricedCount || metadata.priced || 0)
      }
      if (type === 'noop') {
        return Number(metadata.noopCount || metadata.noop || 0)
      }
      return 0
    },
    getMapApiFailureCount (item) {
      const metadata = item && item.decisionMetadata ? item.decisionMetadata : {}
      return Number(metadata.mapApiFailureCount || metadata.mapApiFailures || 0)
    },
    async resetRow (item) {
      if (!item || !item.transportationProfileId) {
        return
      }
      this.resettingId = item.transportationProfileId
      try {
        await resetApiProgressivePricingQueue(item.transportationProfileId)
        addAlert({ type: 'success', message: 'Pricing queue row reset to pending' })
        await this.loadData(false)
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to reset pricing queue row'
        })
      } finally {
        this.resettingId = null
      }
    }
  }
}
</script>
